import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

// Rows load one by one; status counts publish once every site in the search scope is checked. The super admin page
// also scopes it to a tenant or a cause.
export default class extends Controller {
  static values = {
    status: String,
    view: { type: String, default: "table" },
    sort: { type: String, default: "name" },
    // Rows outside the tenant or cause are hidden and uncounted; blank for all.
    tenant: String,
    cause: String,
    direction: { type: String, default: "asc" },
    // CommandCenterBoard::STATUS_SORT_RANK["unavailable"], so a failed request sorts like a server-side failure.
    unavailableRank: Number,
  };
  static targets = ["row", "card", "empty", "attentionCount", "healthyCount", "filter", "refresh",
    "tableView", "gridView", "viewToggle", "sortHeader", "clearSearch"];

  connect() {
    this.queue = new Set();
    this.requests = new Map();
    // Queue everything before draining, so in-scope rows go first.
    this.rowTargets.forEach((row) => this.watch(row));
    this.prioritize();
    this.connected = true;
    this.updateView();
    this.syncState();
    this.update();
    this.appliedScope = this.scopeKey;
    this.drain();
  }

  // Grid cards and table rows share a location id under different prefixes.
  static rowIdFor(card) { return card.id.replace("pos-cc-grid-", "pos-cc-card-"); }

  static plainClick(event) {
    return event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  disconnect() {
    this.connected = false;
    this.requests?.forEach((controller) => controller.abort());
  }

  rowTargetConnected(row) {
    // applySort() reconnects rows it moves.
    if (!this.connected || this.reordering) return;
    this.watch(row);
    this.update();
  }

  watch(row) {
    if (row.dataset.siteStatus === "pending") this.enqueue(row);
  }

  cardTargetConnected() {
    if (this.connected && !this.reordering) this.update();
  }

  switchView(event) {
    if (!this.constructor.plainClick(event)) return;
    event.preventDefault();
    this.viewValue = event.currentTarget.dataset.siteView;
    this.updateView();
    this.syncState();
  }

  updateView() {
    this.tableViewTarget.classList.toggle("hidden", this.viewValue === "grid");
    this.gridViewTarget.classList.toggle("hidden", this.viewValue !== "grid");
    this.viewToggleTargets.forEach((toggle) => {
      toggle.setAttribute("aria-current", String(toggle.dataset.siteView === this.viewValue));
    });
  }

  // The one place browser-side state (status, layout, sort) is written back: the search form's hidden inputs,
  // every toolbar link (rendered with the server's state), the sort header arrows, the frame URL Mo's
  // reload_frame uses, and the address bar. Links leave defaults off; the address bar spells out a sorted
  // column's direction so the URL reads unambiguously.
  syncState() {
    const state = { status: this.statusValue, view: this.viewValue, sort: this.sortValue, dir: this.directionValue };
    const defaults = { status: "all", view: "table", sort: "name", dir: "asc", tenant: "", cause: "" };
    if (this.hasTenantValue) state.tenant = this.tenantValue;
    if (this.hasCauseValue) state.cause = this.causeValue;
    const write = (url, overrides = {}, { explicitDir = false } = {}) => {
      Object.entries({ ...state, ...overrides }).forEach(([name, value]) => {
        if (value === undefined) return;
        const keep = value !== defaults[name] || (explicitDir && name === "dir" && state.sort !== defaults.sort);
        if (keep) url.searchParams.set(name, value);
        else url.searchParams.delete(name);
      });
      return url;
    };
    const rewrite = (link, overrides) => { link.href = write(new URL(link.href), overrides).toString(); };

    Object.entries(state).forEach(([name, value]) => {
      const input = this.element.querySelector(`input[name='${name}']`);
      if (input) input.value = value;
    });
    this.viewToggleTargets.forEach((toggle) => rewrite(toggle, { view: toggle.dataset.siteView }));
    this.filterTargets.forEach((link) => rewrite(link, { status: undefined }));
    this.clearSearchTargets.forEach((link) => rewrite(link));
    rewrite(this.refreshTarget);
    this.element.closest("turbo-frame").dataset.reloadSrc = this.refreshTarget.href;

    const ascending = this.directionValue === "asc";
    this.sortHeaderTargets.forEach((header) => {
      const active = header.dataset.sortKey === this.sortValue;
      header.closest("th")?.setAttribute("aria-sort", active ? (ascending ? "ascending" : "descending") : "none");
      header.querySelector("[data-sort-icon='asc']").classList.toggle("hidden", !(active && ascending));
      header.querySelector("[data-sort-icon='desc']").classList.toggle("hidden", !(active && !ascending));
      header.querySelector("[data-sort-icon='idle']").classList.toggle("hidden", active);
    });
    const address = write(new URL(window.location.href), {}, { explicitDir: true });
    window.history.replaceState(window.history.state, "", address);
  }

  // A second click on the active column flips it; a new column starts ascending (worst status, quietest site
  // first). Rows without a value for the column (still loading, no cameras) stay at the bottom.
  sort(event) {
    const key = event.currentTarget.dataset.sortKey;
    if (key === this.sortValue) {
      this.directionValue = this.directionValue === "asc" ? "desc" : "asc";
    } else {
      this.sortValue = key;
      this.directionValue = "asc";
    }
    this.applySort();
    this.syncState();
  }

  applySort() {
    const key = this.sortValue;
    const sign = this.directionValue === "desc" ? -1 : 1;
    const attr = `sort${key[0].toUpperCase()}${key.slice(1)}`;
    const value = (row) => (row.dataset.siteStatus === "pending" && key !== "name" ? undefined : row.dataset[attr]);
    const current = this.rowTargets;
    const rows = [...current].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === undefined && bv === undefined) return a.dataset.sortName.localeCompare(b.dataset.sortName);
      if (av === undefined) return 1;
      if (bv === undefined) return -1;
      const cmp = key === "name" ? av.localeCompare(bv) : Number(av) - Number(bv);
      return cmp === 0 ? a.dataset.sortName.localeCompare(b.dataset.sortName) : cmp * sign;
    });
    const tbody = rows[0]?.parentElement;
    const inOrder = rows.every((row, index) => row === current[index]);
    if (tbody && !inOrder) {
      // Stimulus reports moved targets as reconnected from a MutationObserver microtask queued at the first move,
      // so the flag has to outlive this call by one microtask.
      this.reordering = true;
      rows.forEach((row) => tbody.appendChild(row));
      const cards = this.cardsByRowId();
      const grid = this.cardTargets[0]?.parentElement;
      if (grid) rows.forEach((row) => { const card = cards.get(row.id); if (card) grid.appendChild(card); });
      queueMicrotask(() => { this.reordering = false; });
    }
  }

  cardsByRowId() {
    return new Map(this.cardTargets.map((card) => [this.constructor.rowIdFor(card), card]));
  }

  filter(event) {
    if (!this.constructor.plainClick(event)) return;
    event.preventDefault();
    this.statusValue = event.currentTarget.dataset.siteFilter;
    this.applyScope();
  }

  enqueue(row) {
    if (row.dataset.siteStatus !== "pending" || this.requests.has(row.id)) return;
    this.queue.add(row);
    this.drain();
  }

  prioritize() {
    const rows = [...this.queue];
    this.queue = new Set([...rows.filter((row) => this.inScope(row)), ...rows.filter((row) => !this.inScope(row))]);
  }

  drain() {
    if (!this.connected) return;
    while (this.requests.size < 4 && this.queue.size) {
      const row = this.queue.values().next().value;
      this.queue.delete(row);
      if (row.isConnected && row.dataset.siteStatus === "pending") this.load(row);
    }
  }

  retry(event) {
    const item = event.currentTarget.closest("[data-admin--pos--site-table-target]");
    const row = this.rowTargets.find((candidate) => candidate.id === this.constructor.rowIdFor(item));
    if (this.requests.has(row.id)) return;
    row.dataset.siteStatus = "pending";
    row.querySelector("[data-site-error]")?.classList.add("hidden");
    row.querySelector("[data-site-loading]")?.classList.remove("hidden");
    this.enqueue(row);
    this.update();
  }

  async load(row) {
    const controller = new AbortController();
    this.requests.set(row.id, controller);
    row.setAttribute("aria-busy", "true");
    try {
      const response = await get(row.dataset.siteUrl, {
        responseKind: "turbo-stream", signal: controller.signal,
      });
      if (!response.ok || !response.isTurboStream) throw new Error("Could not load site");
    } catch (error) {
      if (error.name !== "AbortError" && row.isConnected) {
        row.dataset.siteStatus = "unavailable";
        row.dataset.sortStatus = String(this.unavailableRankValue);
        row.querySelector("[data-site-loading]")?.classList.add("hidden");
        row.querySelector("[data-site-error]")?.classList.remove("hidden");
      }
    } finally {
      // A cached page may reconnect while an aborted request is finishing.
      if (this.requests.get(row.id) === controller) {
        row.removeAttribute("aria-busy");
        this.requests.delete(row.id);
        if (this.connected) {
          this.update();
          this.drain();
        }
      }
    }
  }

  update() {
    let matches = 0;
    let pending = 0;
    let attention = 0;
    let healthy = 0;
    const cards = this.cardsByRowId();
    this.rowTargets.forEach((row) => {
      if (!this.inScope(row)) {
        row.hidden = true;
        return;
      }
      const status = row.dataset.siteStatus;
      const loading = status === "pending";
      const needsAttention = ["fail", "warn", "unavailable"].includes(status);
      if (needsAttention) attention += 1;
      if (status === "ok") healthy += 1;
      const match = this.statusValue === "all" ||
        (this.statusValue === "healthy" && status === "ok") ||
        (this.statusValue === "attention" && needsAttention);
      row.hidden = !loading && !match;
      const card = cards.get(row.id);
      if (card) {
        card.hidden = row.hidden;
        card.setAttribute("aria-busy", String(loading));
        card.querySelector("[data-site-loading]")?.classList.toggle("hidden", !loading);
        card.querySelector("[data-site-error]")?.classList.toggle("hidden", status !== "unavailable");
      }
      if (loading) pending += 1;
      else if (match) matches += 1;
    });
    const title = pending ? "More locations are being checked" : "Locations matching your search";
    const counts = { attentionCount: attention, healthyCount: healthy };
    Object.entries(counts).forEach(([name, count]) => {
      this[`${name}Targets`].forEach((element) => {
        element.textContent = pending ? "…" : count.toLocaleString();
        element.title = title;
      });
    });
    this.emptyTarget.classList.toggle("hidden", matches + pending > 0);
    this.applySort();
    this.dispatch("updated");
  }

  inScope(row) {
    const tenantMatch = !this.tenantValue || row.dataset.tenantId === this.tenantValue;
    const causeMatch = !this.causeValue || row.dataset.siteStatus === "pending" ||
      (row.dataset.causes || "").split(" ").includes(this.causeValue);
    return tenantMatch && causeMatch;
  }

  get scopeKey() {
    return [this.tenantValue, this.causeValue, this.statusValue].join("|");
  }

  // Value-changed callbacks arrive a tick late; callers that need the rows now call this after setting values.
  applyScope() {
    if (!this.connected || this.scopeKey === this.appliedScope) return;
    this.appliedScope = this.scopeKey;
    this.filterTargets.forEach((filter) => {
      filter.setAttribute("aria-current", String(filter.dataset.siteFilter === this.statusValue));
    });
    this.prioritize();
    this.syncState();
    this.update();
    this.drain();
  }

  tenantValueChanged() {
    this.applyScope();
  }

  causeValueChanged() {
    this.applyScope();
  }

  statusValueChanged() {
    this.applyScope();
  }
}
